import { DotsVertical, MessageTextSquare02, Table as TableIcon } from "@untitledui/icons";
import { Camera } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { UnboxTable } from "@/components/application/table/unbox-table";
import type { UnboxTableColumn, UnboxTableProps } from "@/components/application/table/unbox-table.types";
import { ActionRecordDialog } from "@/components/assurance/action-record-dialog";
import { AdThumbnailBadge } from "@/components/assurance/badges/ad-campaign-badge";
import { DetailedVideoStatusBadge } from "@/components/assurance/badges/video-status-badge";
import { InviteToCollaborationModal } from "@/components/assurance/invite-to-collaboration-modal";
import { VideoActionsMenuItems } from "@/components/assurance/video-action-menus";
import { Avatar } from "@/components/base/avatar/avatar";
import { Badge } from "@/components/base/badges/badges";
import { ButtonUtility } from "@/components/base/buttons/button-utility";
import { BlockCreatorModal } from "@/components/blocklist/block-creator-modal";
import { MessageCreatorsDialog } from "@/components/message-creator-dialogs";
import { Dialog } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useCurrency } from "@/hooks/use-currency";
import { useIsAssurance } from "@/hooks/use-is-assurance";
import type { ActionType } from "@/lib/assurance/action-types";
import { BulkCohortModal } from "@/pages/creator-cohorts/bulk-cohort-modal";
import { AddtoListsModal } from "@/pages/creator-lists/add-to-lists-modal";
import { TrendBadge } from "@/pages/explorers/utils";
import { formatDate, formatPostedAgo } from "@/utils/date-conversion";
import { useVideoColumnCatalog } from "./video-columns";
import type { VideoRow } from "./video-explorer";

const videoFormatter = new Intl.NumberFormat("en-US", {
  compactDisplay: "short",
  notation: "compact",
});

const percentFormatter = new Intl.NumberFormat("en-US", { style: "percent", maximumFractionDigits: 1 });

export interface VideoExplorerTableProps
  extends Omit<
    UnboxTableProps<VideoRow>,
    "columns" | "rowId" | "emptyState" | "filteredEmptyState" | "size" | "rounded" | "className" | "aria-label"
  > {
  timePeriod: "7d" | "30d" | "all_time";
  blocklistEnabled: boolean;
  adsConnected: boolean;
  onClearFilters: () => void;
}

export function VideoExplorerTable({
  rows,
  timePeriod,
  blocklistEnabled,
  adsConnected,
  onClickRow,
  selection,
  sorting,
  onHiddenSortColumn,
  pagination,
  hasActiveFilters,
  onClearFilters,
}: VideoExplorerTableProps) {
  const isAssurance = useIsAssurance();
  const currency = useCurrency();
  const columnCatalog = useVideoColumnCatalog({ isAssurance, adsConnected });

  const [rowActionVideoId, setRowActionVideoId] = useState<string>();
  const [rowActionCreatorId, setRowActionCreatorId] = useState<string>();
  const [actionModalOpen, setActionModalOpen] = useState(false);
  const [preselectedAction, setPreselectedAction] = useState<ActionType | undefined>();
  const [preselectedCategory, setPreselectedCategory] = useState<"take" | "log" | undefined>();
  const [addToListsCreatorId, setAddToListsCreatorId] = useState<string>();
  const [rowMessageCreatorId, setRowMessageCreatorId] = useState<string>();
  const [rowInviteCreatorId, setRowInviteCreatorId] = useState<string>();
  const [rowBlockCreatorId, setRowBlockCreatorId] = useState<string>();
  const [rowCohortCreatorId, setRowCohortCreatorId] = useState<string>();

  const handleRemoveFromCollaboration = useCallback(
    (videoId: VideoRow["videoId"], creatorId: VideoRow["creatorId"]) => {
      setRowActionVideoId(videoId);
      setRowActionCreatorId(creatorId);
      setPreselectedAction("remove_from_collaboration");
      setPreselectedCategory("take");
      setActionModalOpen(true);
    },
    [],
  );
  const periodLabel = timePeriod === "all_time" ? "All Time" : `Last ${timePeriod === "7d" ? 7 : 30} Days`;
  const gmvLabel = `GMV (${periodLabel})`;
  const viewsLabel = `Views (${periodLabel})`;
  const gmvFormatter = useMemo(
    () => new Intl.NumberFormat("en-US", { style: "currency", currency, notation: "compact", compactDisplay: "short" }),
    [currency],
  );
  const gmvPerViewFormatter = useMemo(
    () => new Intl.NumberFormat("en-US", { style: "currency", currency, maximumFractionDigits: 2 }),
    [currency],
  );

  const columns = useMemo<UnboxTableColumn<VideoRow>[]>(
    () => [
      {
        id: "videoTitle",
        label: "Video",
        sortable: true,
        minWidth: "280px",
        render: (_value, row) => (
          <div className="flex flex-row items-center gap-2 min-w-0">
            <div className="relative flex-shrink-0">
              {row.thumbnail ? (
                <img
                  src={row.thumbnail ?? undefined}
                  alt={`${row.videoId} thumnail`}
                  className="h-10 w-10 rounded object-cover"
                />
              ) : (
                <div className="flex h-10 w-10 items-center justify-center rounded bg-gray-100">
                  <span className="text-xs font-medium text-gray-800">
                    <Camera className="h-4 w-6" />
                  </span>
                </div>
              )}
              {row.isInAdCampaign && <AdThumbnailBadge />}
            </div>
            <div className="flex flex-col min-w-0">
              <span
                className="text-sm font-medium text-primary truncate max-w-[200px] xl:max-w-[300px]"
                title={row.videoTitle || ""}
              >
                {row.videoTitle}
              </span>
              {row.creatorName && (
                <span className="text-xs text-gray-500 truncate max-w-[200px] xl:max-w-[300px]" title={row.creatorName}>
                  @{row.creatorName}
                </span>
              )}
            </div>
          </div>
        ),
      },
      {
        id: "gmv",
        label: gmvLabel,
        sortable: true,
        minWidth: "120px",
        render: (_value, row) => (
          <div className="flex flex-row gap-1 items-center flex-wrap font-medium whitespace-nowrap">
            {gmvFormatter.format(row.gmv ?? 0)}
            <TrendBadge change={row.gmvChange} timePeriod={timePeriod} />
          </div>
        ),
      },
      {
        id: "unitSales",
        label: "Unit Sales",
        sortable: true,
        minWidth: "110px",
        render: (_value, row) => (
          <div className="flex flex-row gap-1 items-center flex-wrap font-medium whitespace-nowrap">
            {videoFormatter.format(row.unitSales ?? 0)}
            <TrendBadge change={row.unitSalesChange} timePeriod={timePeriod} />
          </div>
        ),
      },
      {
        id: "views",
        label: viewsLabel,
        sortable: true,
        minWidth: "110px",
        render: (_value, row) => (
          <div className="flex flex-col gap-0.5 whitespace-nowrap">
            <div className="flex flex-row gap-1 items-center flex-wrap font-medium">
              {videoFormatter.format(row.views ?? 0)}
              <TrendBadge change={row.viewsChange} timePeriod={timePeriod} />
            </div>
            {row.publishDate && (
              <span className="text-xs text-quaternary">Posted {formatPostedAgo(row.publishDate)}</span>
            )}
          </div>
        ),
      },
      ...(isAssurance
        ? [
            {
              id: "status",
              label: "Safety Status",
              sortable: true,
              minWidth: "140px",
              render: (_value, row) =>
                row.status ? (
                  <div className="whitespace-nowrap">
                    <DetailedVideoStatusBadge
                      status={row.status}
                      reviewedBy={
                        row.manuallyReviewedByName && row.manuallyReviewedAt
                          ? { name: row.manuallyReviewedByName, at: row.manuallyReviewedAt }
                          : undefined
                      }
                    />
                  </div>
                ) : null,
            } satisfies UnboxTableColumn<VideoRow>,
          ]
        : []),
      {
        id: "publishDate",
        label: "Published",
        sortable: true,
        minWidth: "100px",
        render: (_value, row) => (
          <span className="font-medium whitespace-nowrap">{row.publishDate ? formatDate(row.publishDate) : "—"}</span>
        ),
      },
      {
        id: "gmvTrend",
        label: "GMV Trend %",
        sortable: true,
        minWidth: "120px",
        render: (_value, row) => <TrendBadge change={row.gmvChange} timePeriod={timePeriod} />,
      },
      {
        id: "gpm",
        label: "GPM",
        sortable: true,
        minWidth: "100px",
        render: (_value, row) => (
          <span className="font-medium whitespace-nowrap">
            {row.gmvPerView == null ? "—" : gmvFormatter.format(row.gmvPerView * 1000)}
          </span>
        ),
      },
      {
        id: "conversion",
        label: "Conversion",
        sortable: true,
        minWidth: "110px",
        render: (_value, row) => (
          <span className="font-medium whitespace-nowrap">
            {row.conversion == null ? "—" : percentFormatter.format(row.conversion)}
          </span>
        ),
      },
      {
        id: "gmvPerView",
        label: "GMV per View",
        sortable: true,
        minWidth: "120px",
        render: (_value, row) => (
          <span className="font-medium whitespace-nowrap">
            {row.gmvPerView == null ? "—" : gmvPerViewFormatter.format(row.gmvPerView)}
          </span>
        ),
      },
      {
        id: "viewsTrend",
        label: "Views Trend %",
        sortable: true,
        minWidth: "120px",
        render: (_value, row) => <TrendBadge change={row.viewsChange} timePeriod={timePeriod} />,
      },
      {
        id: "creator",
        label: "Creator",
        minWidth: "180px",
        render: (_value, row) =>
          row.creatorName ? (
            <div className="flex min-w-0 items-center gap-2">
              <Avatar size="xs" src={row.creatorAvatar} alt={row.creatorName} initials={row.creatorName.slice(0, 2)} />
              <span className="truncate text-sm font-medium text-primary" title={row.creatorName}>
                {row.creatorName}
              </span>
            </div>
          ) : (
            "—"
          ),
      },
      {
        id: "creatorFollowers",
        label: "Creator Followers",
        minWidth: "140px",
        render: (_value, row) => (
          <span className="font-medium whitespace-nowrap">
            {row.creatorFollowers == null ? "—" : videoFormatter.format(row.creatorFollowers)}
          </span>
        ),
      },
      {
        id: "daysSincePublished",
        label: "Days Since Published",
        sortable: true,
        minWidth: "160px",
        render: (_value, row) => <span className="font-medium whitespace-nowrap">{row.daysSincePublished}</span>,
      },
      {
        id: "productsPromoted",
        label: "Products Promoted",
        sortable: true,
        minWidth: "140px",
        render: (_value, row) => <span className="font-medium whitespace-nowrap">{row.productsPromoted}</span>,
      },
      {
        id: "lastContacted",
        label: "Last Contacted",
        minWidth: "130px",
        render: (_value, row) => (
          <span className="font-medium whitespace-nowrap">
            {row.creatorContactedAt ? formatDate(row.creatorContactedAt) : "—"}
          </span>
        ),
      },
      ...(adsConnected
        ? [
            {
              id: "adFlag",
              label: "Ad flag",
              minWidth: "90px",
              render: (_value, row) =>
                row.isInAdCampaign ? (
                  <Badge size="sm" color="brand">
                    Ad
                  </Badge>
                ) : (
                  "—"
                ),
            } satisfies UnboxTableColumn<VideoRow>,
          ]
        : []),
      ...(isAssurance
        ? [
            {
              id: "risks",
              label: "Risks Identified",
              minWidth: "160px",
              render: (_value, row) =>
                row.flagCount > 0 ? (
                  <span className="font-medium" title={row.flagTypes.join(", ")}>
                    {row.flagCount} · {row.flagTypes.join(", ")}
                  </span>
                ) : (
                  "—"
                ),
            } satisfies UnboxTableColumn<VideoRow>,
          ]
        : []),
      {
        id: "row-actions",
        label: "",
        minWidth: "60px",
        render: (_value, row) => (
          <div className="flex flex-row gap-1 items-center justify-end">
            <ButtonUtility
              size="xs"
              color="tertiary"
              tooltip="Message Creator on TikTok"
              icon={MessageTextSquare02}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                setRowMessageCreatorId(row.creatorId ?? undefined);
              }}
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="flex items-center justify-center w-8 h-8 hover:bg-secondary rounded transition-colors"
                  onClick={(e) => e.stopPropagation()}
                  aria-label="Video actions"
                >
                  <DotsVertical className="w-5 h-5 text-tertiary" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                collisionPadding={16}
                className="w-[280px] max-h-[min(70vh,500px)] overflow-x-hidden overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <VideoActionsMenuItems
                  handlers={{
                    onMessageCreator: () => setRowMessageCreatorId(row.creatorId ?? undefined),
                    onAddToList: () => setAddToListsCreatorId(row.creatorId ?? undefined),
                    onInviteToCollaboration: () => setRowInviteCreatorId(row.creatorId ?? undefined),
                    onAddToCohort: () => setRowCohortCreatorId(row.creatorId ?? undefined),
                    onBlock: () => setRowBlockCreatorId(row.creatorId ?? undefined),
                    onRemoveFromCollaboration: () => handleRemoveFromCollaboration(row.videoId, row.creatorId),
                    cohortsEnabled: true,
                    blocklistEnabled: blocklistEnabled && !!row.tiktokCreatorOpenId,
                  }}
                />
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ),
      },
    ],
    [
      gmvLabel,
      viewsLabel,
      gmvFormatter,
      gmvPerViewFormatter,
      timePeriod,
      isAssurance,
      adsConnected,
      blocklistEnabled,
      handleRemoveFromCollaboration,
    ],
  );

  return (
    <>
      <UnboxTable<VideoRow>
        aria-label="Video Explorer"
        columns={columns}
        rows={rows}
        rowId="videoId"
        onClickRow={onClickRow}
        selection={selection}
        sorting={sorting}
        columnCatalog={columnCatalog}
        onHiddenSortColumn={onHiddenSortColumn}
        pagination={pagination}
        hasActiveFilters={hasActiveFilters}
        emptyState={{ icon: TableIcon, title: "Nothing to see here yet" }}
        filteredEmptyState={{ onClearFilters }}
        size="sm"
        className="max-h-[calc(60vh)]"
      />

      <Dialog
        open={actionModalOpen}
        onOpenChange={(open) => {
          setActionModalOpen(open);
          if (!open) {
            setPreselectedAction(undefined);
            setPreselectedCategory(undefined);
            setRowActionVideoId(undefined);
            setRowActionCreatorId(undefined);
          }
        }}
      >
        {rowActionVideoId && (
          <ActionRecordDialog
            videos={[
              {
                videoId: rowActionVideoId,
                creatorId: rowActionCreatorId ?? null,
                thumbnail: rows?.find((v) => v.videoId === rowActionVideoId)?.thumbnail ?? null,
                productList: rows?.find((v) => v.videoId === rowActionVideoId)?.productList,
              },
            ]}
            onClose={() => {
              setActionModalOpen(false);
              setPreselectedAction(undefined);
              setPreselectedCategory(undefined);
              setRowActionVideoId(undefined);
              setRowActionCreatorId(undefined);
            }}
            preselectedAction={preselectedAction}
            preselectedCategory={preselectedCategory}
          />
        )}
      </Dialog>

      {rowInviteCreatorId && (
        <InviteToCollaborationModal
          creators={
            rows
              ?.filter((v) => v.creatorId === rowInviteCreatorId && v.tiktokCreatorOpenId)
              .slice(0, 1)
              .map((v) => ({
                openId: v.tiktokCreatorOpenId as string,
                username: v.creatorName ?? "",
                nickname: v.creatorName,
                avatar: v.thumbnail,
              })) || []
          }
          open={!!rowInviteCreatorId}
          onOpenChange={(open) => !open && setRowInviteCreatorId(undefined)}
        />
      )}

      {rowBlockCreatorId && (
        <BlockCreatorModal
          creatorOpenIds={
            rows
              ?.filter((v) => v.creatorId === rowBlockCreatorId && v.tiktokCreatorOpenId)
              .slice(0, 1)
              .map((v) => v.tiktokCreatorOpenId as string) || []
          }
          creatorName={rows?.find((v) => v.creatorId === rowBlockCreatorId)?.creatorName ?? undefined}
          open={!!rowBlockCreatorId}
          onOpenChange={(open) => !open && setRowBlockCreatorId(undefined)}
        />
      )}

      <AddtoListsModal
        selectedCreators={addToListsCreatorId ? [addToListsCreatorId] : []}
        onAddToList={() => setAddToListsCreatorId(undefined)}
        open={addToListsCreatorId !== undefined}
        onOpenChange={(open) => {
          if (!open) setAddToListsCreatorId(undefined);
        }}
      />

      <BulkCohortModal
        creatorIds={rowCohortCreatorId ? [rowCohortCreatorId] : []}
        open={rowCohortCreatorId !== undefined}
        onOpenChange={(open) => {
          if (!open) setRowCohortCreatorId(undefined);
        }}
        onApplied={() => setRowCohortCreatorId(undefined)}
      />

      {rowMessageCreatorId && (
        <MessageCreatorsDialog
          recipients={[
            {
              creatorId: rowMessageCreatorId,
              handle: rows?.find((v) => v.creatorId === rowMessageCreatorId)?.creatorName ?? null,
              thumbnail: rows?.find((v) => v.creatorId === rowMessageCreatorId)?.thumbnail ?? null,
            },
          ]}
          open={!!rowMessageCreatorId}
          onOpenChange={(open) => !open && setRowMessageCreatorId(undefined)}
          onSuccess={() => setRowMessageCreatorId(undefined)}
        />
      )}
    </>
  );
}
